import type { Me } from "@brandwall/contracts";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { ApiError, api } from "./api";
import { Shell } from "./components/Shell";
import { AcceptInvite } from "./pages/AcceptInvite";
import { AcceptTerms } from "./pages/AcceptTerms";
import { AdminBlog } from "./pages/AdminBlog";
import { AdminBlogPost } from "./pages/AdminBlogPost";
import { AdminDemoShots } from "./pages/AdminDemoShots";
import { AdminDemos } from "./pages/AdminDemos";
import { Ai } from "./pages/Ai";
import { Approval } from "./pages/Approval";
import { Collecting } from "./pages/Collecting";
import { Connect } from "./pages/Connect";
import { Consent } from "./pages/Consent";
import { Demo } from "./pages/Demo";
import { DemoReport } from "./pages/DemoReport";
import { Discover } from "./pages/Discover";
import { EmailAction } from "./pages/EmailAction";
import { Home } from "./pages/Home";
import { Inbox } from "./pages/Inbox";
import { Insights } from "./pages/Insights";
import { Landing } from "./pages/Landing";
import { Login } from "./pages/Login";
import { NewBrand } from "./pages/NewBrand";
import { Products } from "./pages/Products";
import { Rights } from "./pages/Rights";
import { Seasons } from "./pages/Seasons";
import { Settings } from "./pages/Settings";
import { Sources } from "./pages/Sources";
import { SuperAdmin } from "./pages/SuperAdmin";
import { Taste } from "./pages/Taste";
import { WallEditor } from "./pages/WallEditor";
import { Walls } from "./pages/Walls";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/v1/me") });
}

function RequireLogin({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <div className="onb muted">Loading…</div>;
  if (me.error instanceof ApiError && me.error.status === 401) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  }
  if (me.error) return <div className="onb error">{me.error.message}</div>;
  if (me.data && !me.data.user.termsAccepted) return <AcceptTerms />;
  return <>{children}</>;
}

function Start() {
  const me = useMe();
  if (!me.data) return null;
  const first = me.data.workspaces[0];
  return <Navigate to={first ? `/w/${first.id}` : "/new"} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/r/:token" element={<Approval />} />
      <Route path="/e/:token" element={<EmailAction />} />
      <Route path="/demo/:token" element={<Demo />} />
      <Route path="/demo/:token/report" element={<DemoReport />} />
      <Route path="/for/:token" element={<Landing />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route
        path="/oauth/authorize"
        element={
          <RequireLogin>
            <Consent />
          </RequireLogin>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireLogin>
            <SuperAdmin />
          </RequireLogin>
        }
      />
      <Route
        path="/admin/demos"
        element={
          <RequireLogin>
            <AdminDemos />
          </RequireLogin>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <RequireLogin>
            <AdminBlog />
          </RequireLogin>
        }
      />
      <Route
        path="/admin/blog/:id"
        element={
          <RequireLogin>
            <AdminBlogPost />
          </RequireLogin>
        }
      />
      <Route
        path="/admin/demos/:id/shots"
        element={
          <RequireLogin>
            <AdminDemoShots />
          </RequireLogin>
        }
      />
      <Route
        path="/"
        element={
          <RequireLogin>
            <Start />
          </RequireLogin>
        }
      />
      <Route
        path="/new"
        element={
          <RequireLogin>
            <NewBrand />
          </RequireLogin>
        }
      />
      <Route
        path="/w/:ws/connect"
        element={
          <RequireLogin>
            <Connect />
          </RequireLogin>
        }
      />
      <Route
        path="/w/:ws/collecting"
        element={
          <RequireLogin>
            <Collecting />
          </RequireLogin>
        }
      />
      <Route
        path="/w/:ws"
        element={
          <RequireLogin>
            <Shell />
          </RequireLogin>
        }
      >
        <Route index element={<Home />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="sources" element={<Sources />} />
        <Route path="walls" element={<Walls />} />
        <Route path="walls/:id" element={<WallEditor />} />
        <Route path="discover" element={<Discover />} />
        <Route path="rights" element={<Rights />} />
        <Route path="products" element={<Products />} />
        <Route path="taste" element={<Taste />} />
        <Route path="settings" element={<Settings />} />
        <Route path="seasons" element={<Seasons />} />
        <Route path="ai" element={<Ai />} />
        <Route path="insights" element={<Insights />} />
      </Route>
      <Route
        path="*"
        element={
          <div className="onb">
            <div className="box card stack">
              <h1>Page not found</h1>
              <a className="btn" href="/">
                Go to Brandwall
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}
